import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/common";
import { DuplicateTaskDialog, submitWithDuplicateGuard } from "@/components/DuplicateTaskDialog";
import { FileText, Upload, ExternalLink, Download, Search, RefreshCcw } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;

export const DOC_CATEGORIES = ["Identity", "Academic", "Employment", "Financial",
  "English", "Skill Assessment", "Visa", "University", "Contracts/Invoices",
  "Financial Documents", "CV / Resume", "Other",
  "Submission Acknowledgement", "Lodgement Receipt", "Submission Confirmation",
  "Assessing Authority Acknowledgement", "Submission Evidence"];

const PRIORITIES = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["urgent", "Urgent"]];
const TASK_TYPES = [["general", "General"], ["followup_call", "Follow-up call"], ["document", "Document collection"], ["payment_followup", "Payment follow-up"], ["onboarding", "Onboarding"], ["other", "Other"]];
const TEAMS = [["", "No team"], ["sales", "Sales"], ["process", "Process"], ["finance", "Finance"], ["support", "Support"]];

/* =========================================================================
   FULL Add Task dialog — title, description, due date+time, priority (incl.
   Urgent), assignee (active users), team, related opportunity/case, type,
   notes. Appears in C360 + assignee work queue + linked case/opportunity.
   ========================================================================= */
export const AddTaskDialog = ({ open, setOpen, contactId, opportunities = [], cases = [], defaultOppId, onCreated }) => {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ title: "", description: "", due_date: "", due_time: "10:00", priority: "medium", assignee: "", team: "", opportunity_id: defaultOppId || "", case_id: "", task_type: "general", notes: "" });
  const [saving, setSaving] = useState(false);
  const [dupConflict, setDupConflict] = useState(null);
  useEffect(() => {
    if (open) {
      api.get("/users").then((r) => setUsers((r.data.users || r.data).filter((u) => u.is_active !== false)));
      setF((p) => ({ ...p, opportunity_id: defaultOppId || p.opportunity_id }));
    } else {
      setDupConflict(null);
    }
  }, [open, defaultOppId]);
  const submitNewTask = (extra) => {
    const due_at = f.due_date ? `${f.due_date}T${f.due_time || "10:00"}:00+05:30` : null;
    return api.post("/ops/tasks", {
      title: f.title, description: f.description, contact_id: contactId,
      opportunity_id: f.opportunity_id || null, case_id: f.case_id || null,
      priority: f.priority, due_at,
      assignee_user_ids: f.assignee ? [f.assignee] : [],
      team: f.team || null, task_type: f.task_type, notes: f.notes || null,
      ...extra,
    });
  };
  const afterCreate = () => {
    setOpen(false);
    setF({ title: "", description: "", due_date: "", due_time: "10:00", priority: "medium", assignee: "", team: "", opportunity_id: defaultOppId || "", case_id: "", task_type: "general", notes: "" });
    onCreated && onCreated();
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await submitWithDuplicateGuard(submitNewTask);
      if (!r.ok) { setDupConflict(r.conflict); return; }
      toast.success("Task created — visible in Customer 360 and the assignee's work queue");
      afterCreate();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };
  const resolveDuplicate = async (decision, reason) => {
    setSaving(true);
    try {
      await submitNewTask({ duplicate_decision: decision, duplicate_reason: reason });
      toast.success(decision === "cancel" ? "Not created" : decision === "use_and_reschedule"
        ? "Existing task rescheduled" : decision === "replace" ? "Existing task replaced" : "Both tasks kept");
      setDupConflict(null);
      afterCreate();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Add task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Task title *</Label>
            <Input data-testid="task-title-input" placeholder="What needs to be done?" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Description / instructions</Label>
            <Textarea data-testid="task-description-input" rows={2} placeholder="Instructions for the assignee…" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">Due date *</Label>
              <Input data-testid="task-due-date" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Due time (IST)</Label>
              <Input data-testid="task-due-time" type="time" value={f.due_time} onChange={(e) => setF({ ...f, due_time: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="task-priority-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Task type</Label>
              <Select value={f.task_type} onValueChange={(v) => setF({ ...f, task_type: v })}>
                <SelectTrigger data-testid="task-type-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Assign employee</Label>
              <Select value={f.assignee} onValueChange={(v) => setF({ ...f, assignee: v })}>
                <SelectTrigger data-testid="task-assignee-select" className="h-9"><SelectValue placeholder="Me (default)" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.role}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Team</Label>
              <Select value={f.team || "none"} onValueChange={(v) => setF({ ...f, team: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="task-team-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{TEAMS.map(([v, l]) => <SelectItem key={v || "none"} value={v || "none"}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            {opportunities.length > 0 && (
              <div className="space-y-1"><Label className="text-xs">Related opportunity / product</Label>
                <Select value={f.opportunity_id || "none"} onValueChange={(v) => setF({ ...f, opportunity_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="task-opportunity-select" className="h-9"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {opportunities.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_name || o.product_code}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            {cases.length > 0 && (
              <div className="space-y-1"><Label className="text-xs">Related case</Label>
                <Select value={f.case_id || "none"} onValueChange={(v) => setF({ ...f, case_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="task-case-select" className="h-9"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {cases.map((c) => <SelectItem key={c.case_id} value={c.case_id}>{c.case_id} · {c.current_stage}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
          </div>
          <div className="space-y-1"><Label className="text-xs">Notes (optional)</Label>
            <Textarea data-testid="task-notes-input" rows={2} placeholder="Extra context…" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button data-testid="task-create-button" disabled={saving || !f.title.trim() || !f.due_date} onClick={save}>Create task</Button>
        </DialogFooter>
      </DialogContent>
      <DuplicateTaskDialog conflict={dupConflict} busy={saving} onChoose={resolveDuplicate} onCancel={() => setDupConflict(null)} />
    </Dialog>
  );
};

/* =========================================================================
   UPLOAD dialog — real file upload → Google Drive (local fallback with the
   exact missing-permission reason surfaced). Same canonical repo everywhere.
   ========================================================================= */
export const UploadDocumentDialog = ({ open, setOpen, contactId, opportunities = [], cases = [], caseId, versionOf, onUploaded }) => {
  const fileRef = useRef(null);
  const [f, setF] = useState({ category: "Other", description: "", opportunity_id: "", case_id: caseId || "" });
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [dest, setDest] = useState(null); // storage destination (Drive status + folder path)
  useEffect(() => {
    if (open && contactId) {
      setDest(null);
      api.get(`/documents/drive-status?contact_id=${contactId}`).then((r) => setDest(r.data)).catch(() => setDest({ connected: false, message: "Could not read Drive status" }));
    }
  }, [open, contactId]);
  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("contact_id", contactId);
      fd.append("category", f.category);
      fd.append("description", f.description);
      if (f.opportunity_id) fd.append("opportunity_id", f.opportunity_id);
      if (f.case_id) fd.append("case_id", f.case_id);
      if (versionOf) fd.append("document_id", versionOf.document_id);
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.storage === "drive") toast.success(`Uploaded to Google Drive — ${dest?.folder_path || "customer folder"} (file ID ${r.data.drive_file_id})`, { duration: 7000 });
      else toast.warning(`NOT in Drive yet — held in SalesOS fallback storage (Drive ${r.data.drive_status || "pending"}). ${r.data.storage_note?.slice(0, 120)}`, { duration: 9000 });
      setOpen(false); setF({ category: "Other", description: "", opportunity_id: "", case_id: caseId || "" }); setFileName("");
      onUploaded && onUploaded();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">{versionOf ? `Upload new version — ${versionOf.name}` : "Upload document"}</DialogTitle></DialogHeader>
        {/* STORAGE DESTINATION — shown BEFORE upload */}
        <div className="rounded-md border p-2.5 text-xs space-y-1" data-testid="doc-storage-destination">
          <div className="flex items-center gap-2">
            <span className="font-semibold uppercase tracking-wide text-muted-foreground text-[10px]">Storage destination</span>
            {dest === null ? <Badge variant="outline" className="text-[9px]">Checking…</Badge>
              : dest.connected
                ? <Badge variant="outline" className="text-[9px] bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" data-testid="drive-status-connected">Google Drive connected</Badge>
                : <Badge variant="outline" className="text-[9px] bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]" data-testid="drive-status-disconnected">Google Drive NOT connected</Badge>}
          </div>
          {dest && (
            <>
              <p>Storage: <b>{dest.connected ? "Google Drive" : "SalesOS fallback (Drive pending)"}</b></p>
              <p>Customer folder: <b data-testid="dest-customer-folder">{dest.customer_folder}</b></p>
              <p>Folder location: <span className="font-mono" data-testid="dest-folder-path">{dest.folder_path}</span></p>
              {dest.folder_link && <a href={dest.folder_link} target="_blank" rel="noreferrer" className="text-[hsl(var(--info))] hover:underline inline-flex items-center gap-1" data-testid="dest-folder-link"><ExternalLink className="h-3 w-3" />Open customer folder in Drive</a>}
              {dest.will_create_folder && <p className="text-muted-foreground">Folder will be created automatically on first upload (Customer ID + Name rule).</p>}
              {dest.message && <p className={dest.connected ? "text-muted-foreground" : "text-[hsl(var(--warning))]"} data-testid="dest-message">{dest.message}</p>}
            </>
          )}
        </div>
        <div className="space-y-2.5">
          <div className="space-y-1"><Label className="text-xs">File *</Label>
            <Input data-testid="doc-file-input" ref={fileRef} type="file" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} /></div>
          {!versionOf && (<>
            <div className="space-y-1"><Label className="text-xs">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="doc-category-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            {opportunities.length > 0 && (
              <div className="space-y-1"><Label className="text-xs">Related product / opportunity</Label>
                <Select value={f.opportunity_id || "none"} onValueChange={(v) => setF({ ...f, opportunity_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="doc-opportunity-select" className="h-9"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {opportunities.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_name || o.product_code}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            {cases.length > 0 && !caseId && (
              <div className="space-y-1"><Label className="text-xs">Related case</Label>
                <Select value={f.case_id || "none"} onValueChange={(v) => setF({ ...f, case_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="doc-case-select" className="h-9"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {cases.map((c) => <SelectItem key={c.case_id} value={c.case_id}>{c.case_id} · {c.current_stage}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            <div className="space-y-1"><Label className="text-xs">Description / note</Label>
              <Input data-testid="doc-description-input" placeholder="e.g. Passport bio page" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          </>)}
          <p className="text-[11px] text-muted-foreground">{fileName && `Selected: ${fileName}`}</p>
        </div>
        <DialogFooter>
          <Button data-testid="doc-upload-button" disabled={busy || !fileName} onClick={upload}><Upload className="h-4 w-4 mr-1.5" />{busy ? "Uploading…" : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* =========================================================================
   CANONICAL documents workspace — used by Customer 360 AND Process/Cases.
   Upload / open / download / new version / verify / reject / search.
   ========================================================================= */
export const DocumentsWorkspace = ({ contactId, caseId, opportunities = [], cases = [], compact = false }) => {
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [upOpen, setUpOpen] = useState(false);
  const [versionOf, setVersionOf] = useState(null);
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const [drive, setDrive] = useState(null);
  const load = useCallback(() => {
    const p = caseId && !contactId ? `case_id=${caseId}` : `contact_id=${contactId}`;
    api.get(`/documents?${p}`).then((r) => setDocs(r.data));
    if (contactId) api.get(`/documents/drive-status?contact_id=${contactId}`).then((r) => setDrive(r.data)).catch(() => {});
  }, [contactId, caseId]);
  useEffect(() => { load(); }, [load]);
  const retryDrive = async (d) => {
    try {
      const r = await api.post(`/documents/${d.document_id}/retry-drive`);
      toast.success(`Now in Google Drive — file ID ${r.data.drive_file_id}`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const patch = async (id, body, msg) => {
    try { await api.patch(`/documents/${id}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const CLS = { required: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]", uploaded: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]", generated: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]", verified: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]", rejected: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]", expired: "bg-muted text-muted-foreground" };
  const filtered = (docs || []).filter((d) =>
    (cat === "all" || d.category === cat) &&
    (!q.trim() || `${d.name} ${d.filename || ""} ${d.category} ${d.uploaded_by || ""}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <Card><CardContent className="pt-4 space-y-2" data-testid="documents-workspace">
      {drive && (
        <div className={`rounded-md border px-2.5 py-1.5 text-[11px] flex items-center gap-2 flex-wrap ${drive.connected ? "" : "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5"}`} data-testid="documents-drive-banner">
          {drive.connected
            ? <Badge variant="outline" className="text-[9px] bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">Google Drive connected</Badge>
            : <Badge variant="outline" className="text-[9px] bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]">Google Drive not connected</Badge>}
          <span className="font-mono">{drive.folder_path}</span>
          {drive.folder_link && <a href={drive.folder_link} target="_blank" rel="noreferrer" className="text-[hsl(var(--info))] hover:underline inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" />Open folder</a>}
          {!drive.connected && <span className="text-[hsl(var(--warning))]">Uploads are held in fallback storage marked "Drive pending" — retry after connecting Google with the Drive file scope.</span>}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
          <Input data-testid="doc-search-input" className="h-8 pl-7 w-44 text-xs" placeholder="Search documents…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="doc-category-filter" className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="doc-upload-open" size="sm" className="h-8 ml-auto" onClick={() => { setVersionOf(null); setUpOpen(true); }}>
          <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload file
        </Button>
      </div>
      {!docs ? null : filtered.length === 0 ? (
        <EmptyState icon={FileText} title="No documents" hint="Upload the first file — it is saved to the customer's Google Drive folder when connected." testId="docs-empty" />
      ) : filtered.map((d) => (
        <div key={d.document_id} className="p-2.5 rounded-md border space-y-1" data-testid={`doc-${d.document_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{d.name}</span>
            <Badge variant="outline" className="text-[9px]">{d.category}</Badge>
            <Badge variant="outline" className={`text-[10px] capitalize ${CLS[d.status] || ""}`}>{d.status}</Badge>
            {d.version > 0 && <span className="text-[10px] text-muted-foreground">v{d.version}</span>}
            {d.product_code && <Badge variant="outline" className="text-[9px]">{d.product_code}</Badge>}
            {d.case_id && <Badge variant="outline" className="text-[9px]">Case</Badge>}
            {d.storage === "local" && <Badge variant="outline" className="text-[9px] bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]" data-testid={`doc-drive-pending-${d.document_id}`}>Drive {d.drive_status === "failed" ? "failed" : "pending"}</Badge>}
            <div className="ml-auto flex flex-wrap gap-1 items-center max-w-full">
              {d.drive_link && (
                <a href={d.drive_link} target="_blank" rel="noreferrer" data-testid={`doc-open-${d.document_id}`} className="text-xs text-[hsl(var(--info))] hover:underline inline-flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" /> Open in Drive
                </a>
              )}
              {d.storage === "local" && (
                <>
                  <a href={`${backend}/api/documents/${d.document_id}/download`} target="_blank" rel="noreferrer" data-testid={`doc-download-${d.document_id}`} className="text-xs text-[hsl(var(--info))] hover:underline inline-flex items-center gap-1">
                    <Download className="h-3 w-3" /> Download
                  </a>
                  <Button data-testid={`doc-retry-drive-${d.document_id}`} size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => retryDrive(d)}>
                    <RefreshCcw className="h-3 w-3 mr-1" /> Retry to Drive
                  </Button>
                </>
              )}
              {["uploaded", "verified", "rejected"].includes(d.status) && (
                <Button data-testid={`doc-newversion-${d.document_id}`} size="sm" variant="ghost" className="h-6 text-[11px] px-1.5" onClick={() => { setVersionOf(d); setUpOpen(true); }}>
                  <RefreshCcw className="h-3 w-3 mr-1" /> New version
                </Button>
              )}
              {d.status === "uploaded" && (
                <>
                  <Button data-testid={`doc-verify-${d.document_id}`} size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => patch(d.document_id, { status: "verified" }, "Document verified")}>Verify</Button>
                  <Button data-testid={`doc-reject-${d.document_id}`} size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setReject(d)}>Reject</Button>
                </>
              )}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground break-words">
            {d.filename && <span className="font-mono">{d.filename} · </span>}
            {d.uploaded_by ? `by ${d.uploaded_by} · ` : ""}{d.created_at ? fmtIST(d.created_at, { year: true }) : ""}
            {d.drive_file_id ? <span className="font-mono"> · Drive file {d.drive_file_id}</span> : ""}
            {(d.history || []).length > 1 ? ` · ${(d.history || []).map((h) => h.event).join(" → ")}` : ""}
          </p>
          {d.storage_note && <p className="text-[10px] text-[hsl(var(--warning))]">{d.storage_note}</p>}
        </div>
      ))}
      <UploadDocumentDialog open={upOpen} setOpen={setUpOpen} contactId={contactId} opportunities={opportunities} cases={cases} caseId={caseId} versionOf={versionOf} onUploaded={load} />
      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Reject document</DialogTitle></DialogHeader>
          <Input data-testid="doc-reject-reason" placeholder="Reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button data-testid="doc-reject-confirm" variant="destructive" disabled={!reason.trim()}
              onClick={() => { patch(reject.document_id, { status: "rejected", reject_reason: reason }, "Document rejected"); setReject(null); setReason(""); }}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};
